/**
 * PasswordRotationBanner — appears only when the backend signals
 * `force_password_change: true` on /api/users/me. Calm amber tone, one
 * actionable button ("Change password") that opens the shared
 * `ChangePasswordDialog` (Iter 32l — extracted into its own component
 * so the proactive `ProfilePage → Change password` entry point can
 * reuse the exact same modal).
 *
 * Off-by-default for tenants without an explicit password policy:
 * this banner never appears unless backend authoritatively flags it.
 */
import React, { useEffect, useState } from 'react';
import ChangePasswordDialog from './ChangePasswordDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PasswordRotationBanner() {
  const [needsChange, setNeedsChange] = useState(false);
  const [open, setOpen] = useState(false);

  const checkStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/users/me`, { credentials: 'include' });
      if (!res.ok) { setNeedsChange(false); return; }
      const me = await res.json();
      setNeedsChange(!!me.force_password_change);
    } catch {
      // Fail-quiet — never block the dashboard for a status poll.
      setNeedsChange(false);
    }
  };

  useEffect(() => { checkStatus(); }, []);

  if (!needsChange) return null;

  return (
    <>
      <div
        data-testid="password-rotation-banner"
        className="qf-glass-banner qf-glass-banner--amber"
        style={{
          marginBottom: 12,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}
      >
        <div style={{ fontSize: 13, color: '#78350f' }}>
          <strong style={{ fontWeight: 600 }}>Password rotation required.</strong>{' '}
          Your tenant's policy asks you to set a new password.
        </div>
        <button
          type="button"
          className="qf-glass-banner-action"
          onClick={() => setOpen(true)}
          style={{ color: '#78350f', fontSize: 12, fontWeight: 600 }}
          data-testid="password-rotation-banner-action"
        >
          Change password
        </button>
      </div>
      {open && (
        <ChangePasswordDialog
          forceContext
          onClose={() => setOpen(false)}
          onSaved={() => { setNeedsChange(false); }}
        />
      )}
    </>
  );
}
